// src/components/HeaderMenu.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

function HeaderMenu({ isAdmin, onLogout }) {
  const [activeLink, setActiveLink] = useState(window.location.pathname);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [facebookUrl, setFacebookUrl] = useState('https://www.facebook.com/');

  // Function to clear all saved scroll positions and scroll to top
  const clearScrollPositionsAndScrollToTop = () => {
    // Clear any saved scroll positions for all listing pages
    const listingPages = ['/', '/auta', '/nehnutelnosti', '/neplatici'];
    listingPages.forEach(page => {
      sessionStorage.removeItem(`scrollPosition-${page}`);
      sessionStorage.removeItem(`visibleCount-${page}`);
    });
    
    // Scroll to top immediately
    window.scrollTo(0, 0);
  };

  // Enhanced click handler for navigation links
  const handleNavClick = (path) => {
    setActiveLink(path);
    clearScrollPositionsAndScrollToTop();
    if (menuOpen) {
      setMenuOpen(false);
    }
  };

  // Fetch Facebook URL from the API
  useEffect(() => {
    const fetchFacebookUrl = async () => {
      try {
        console.log('Fetching Facebook URL...');
        const response = await fetch('https://eurocentrumv1.onrender.com/api/site-config');
        if (response.ok) {
          const data = await response.json();
          console.log('Received site config:', data);
          if (data.facebookUrl) {
            console.log('Setting Facebook URL to:', data.facebookUrl);
            setFacebookUrl(data.facebookUrl);
          }
        } else {
          console.error('Failed to fetch site config:', response.status);
          // TEMPORARY: Use default Facebook URL while deployment is being fixed
          console.log('Using default Facebook URL due to deployment issues');
          setFacebookUrl('https://www.facebook.com/EurocentrumPd');
        }
      } catch (error) {
        console.error('Error fetching Facebook URL:', error);
        // TEMPORARY: Use default Facebook URL while deployment is being fixed
        console.log('Using default Facebook URL due to deployment issues');
        setFacebookUrl('https://www.facebook.com/EurocentrumPd');
      }
    };

    fetchFacebookUrl();
    
    // Set up interval to check for updates every 30 seconds
    const intervalId = setInterval(fetchFacebookUrl, 30000);
    
    // Clean up interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  // Prevent body scrolling when menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <>
      {/* Full-screen mobile menu (outside the nav structure) */}
      {isMobile && (
        <div style={{
          ...styles.mobileMenuContainer,
          transform: menuOpen ? 'translateX(0)' : 'translateX(-100%)',
        }}>
          {/* Close button */}
          {menuOpen && (
            <button 
              style={styles.closeButton} 
              onClick={toggleMenu}
              aria-label="Close menu"
            >
              ✕
            </button>
          )}

          <ul style={styles.mobileNavList}>
            <li style={styles.mobileNavItem}>
              <a 
                href="/about-us" 
                style={activeLink === '/about-us' ? styles.mobileActiveNavLink : styles.mobileNavLink}
                onClick={() => handleNavClick('/about-us')}
              >
                O nás
              </a>
            </li>
            <li style={styles.mobileNavItem}>
              <a 
                href="/" 
                style={activeLink === '/' ? styles.mobileActiveNavLink : styles.mobileNavLink}
                onClick={() => handleNavClick('/')}
              >
                Aktuálne
              </a>
            </li>
            <li style={styles.mobileNavItem}>
              <a 
                href="/nehnutelnosti" 
                style={activeLink === '/nehnutelnosti' ? styles.mobileActiveNavLink : styles.mobileNavLink}
                onClick={() => handleNavClick('/nehnutelnosti')}
              >
                NEHNUTEĽNOSTI (AJ NA SPLÁTKY)
              </a>
            </li>
            <li style={styles.mobileNavItem}>
              <a 
                href="/auta" 
                style={activeLink === '/auta' ? styles.mobileActiveNavLink : styles.mobileNavLink}
                onClick={() => handleNavClick('/auta')}
              >
                AUTÁ (AJ NA SPLÁTKY)
              </a>
            </li>
            <li style={styles.mobileNavItem}>
              <a 
                href="/neplatici" 
                style={activeLink === '/neplatici' ? styles.mobileActiveNavLink : styles.mobileNavLink}
                onClick={() => handleNavClick('/neplatici')}
              >
                STOP dílerom drog...
              </a>
            </li>
            <li style={styles.mobileNavItem}>
              <a 
                href="/contact" 
                style={activeLink === '/contact' ? styles.mobileActiveNavLink : styles.mobileNavLink}
                onClick={() => handleNavClick('/contact')}
              >
                Kontakt
              </a>
            </li>
            <li style={styles.mobileNavItem}>
              <a 
                href={facebookUrl} 
                style={{...styles.mobileNavLink, ...styles.facebookLink}}
                onClick={() => handleNavClick(facebookUrl)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Facebook
              </a>
            </li>
            
            {/* Admin button in mobile menu */}
            <li style={styles.mobileNavItem}>
              {isAdmin ? (
                <button 
                  style={styles.mobileAdminButton}
                  onClick={() => {
                    onLogout();
                    handleNavClick('/');
                  }}
                >
                  Odhlásiť sa
                </button>
              ) : (
                <a 
                  href="/login" 
                  style={styles.mobileAdminButton}
                  onClick={() => {
                    handleNavClick('/login');
                  }}
                >
                  Admin
                </a>
              )}
            </li>
          </ul>
        </div>
      )}

      {/* Overlay for mobile menu */}
      {isMobile && menuOpen && (
        <div 
          style={styles.overlay}
          onClick={() => handleNavClick('/')}
        ></div>
      )}

      {/* Regular nav bar */}
      <nav style={styles.nav}>
        {/* Logo is positioned to the left and links to homepage */}
        <div style={styles.logoContainer}>
          <a href="/" onClick={() => handleNavClick('/')}>
            <img
              src="/assets/logo.png"
              alt="Eurocentrum Logo"
              style={styles.logo}
            />
          </a>
        </div>

        {/* Hamburger Menu Button for Mobile - only show when menu is closed */}
        {isMobile && !menuOpen && (
          <button 
            style={styles.hamburger} 
            onClick={toggleMenu}
            aria-label="Toggle menu"
          >
            <span style={styles.hamburgerLine}></span>
            <span style={styles.hamburgerLine}></span>
            <span style={styles.hamburgerLine}></span>
          </button>
        )}

        {/* Desktop Navigation Links */}
        {!isMobile && (
          <ul style={styles.navList}>
            <li style={styles.navItem}>
              <a 
                href="/about-us" 
                style={activeLink === '/about-us' ? styles.activeNavLink : styles.navLink}
                onClick={() => handleNavClick('/about-us')}
              >
                O nás
              </a>
            </li>
            <li style={styles.navItem}>
              <a 
                href="/" 
                style={activeLink === '/' ? styles.activeNavLink : styles.navLink}
                onClick={() => handleNavClick('/')}
              >
                Aktuálne
              </a>
            </li>
            <li style={styles.navItem}>
              <a 
                href="/nehnutelnosti" 
                style={activeLink === '/nehnutelnosti' ? styles.activeNavLink : styles.navLink}
                onClick={() => handleNavClick('/nehnutelnosti')}
              >
                NEHNUTEĽNOSTI (AJ NA SPLÁTKY)
              </a>
            </li>
            <li style={styles.navItem}>
              <a 
                href="/auta" 
                style={activeLink === '/auta' ? styles.activeNavLink : styles.navLink}
                onClick={() => handleNavClick('/auta')}
              >
                AUTÁ (AJ NA SPLÁTKY)
              </a>
            </li>
            <li style={styles.navItem}>
              <a 
                href="/neplatici" 
                style={activeLink === '/neplatici' ? styles.activeNavLink : styles.navLink}
                onClick={() => handleNavClick('/neplatici')}
              >
                STOP dílerom drog...
              </a>
            </li>
            <li style={styles.navItem}>
              <a 
                href="/contact" 
                style={activeLink === '/contact' ? styles.activeNavLink : styles.navLink}
                onClick={() => handleNavClick('/contact')}
              >
                Kontakt
              </a>
            </li>
            <li style={styles.navItem}>
              <a 
                href={facebookUrl} 
                style={{...styles.navLink, ...styles.facebookLink}}
                target="_blank"
                rel="noopener noreferrer"
              >
                Facebook
              </a>
            </li>
          </ul>
        )}
        
        {/* Admin Login/Logout button for desktop */}
        {!isMobile && (
          <div style={styles.adminButtonContainer}>
            {isAdmin ? (
              <button style={styles.adminButton} onClick={() => {
                onLogout();
                handleNavClick('/');
              }}>
                Odhlásiť sa
              </button>
            ) : (
              <a href="/login" style={styles.adminButton}>
                Admin
              </a>
            )}
          </div>
        )}
      </nav>
    </>
  );
}

export default HeaderMenu;

const styles = {
  nav: {
    position: 'relative',
    width: '100%',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '80px',
    padding: '0 1rem',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(8px)',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    boxSizing: 'border-box',
    maxWidth: '100vw',
    overflowX: 'hidden',
  },
  logoContainer: {
    position: 'absolute',
    left: '1rem',
    display: 'flex',
    alignItems: 'center',
    zIndex: 10001,
  },
  logo: {
    height: '50px',
    width: 'auto',
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
    maxWidth: '100%',
    cursor: 'pointer',
  },
  hamburger: {
    position: 'absolute',
    right: '1rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    width: '30px',
    height: '21px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    zIndex: 10001,
  },
  hamburgerLine: {
    width: '30px',
    height: '3px',
    backgroundColor: 'white',
    borderRadius: '10px',
    marginBottom: '5px',
  },
  closeButton: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    background: 'transparent',
    border: 'none',
    fontSize: '1.5rem',
    color: 'white',
    cursor: 'pointer',
    zIndex: 10002,
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9990,
  },
  // Mobile menu container
  mobileMenuContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: '#2c69a0', // Blue background
    zIndex: 99999, // Extremely high z-index to be above everything
    transition: 'transform 0.3s ease-in-out',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start', // Start at the top
    alignItems: 'flex-start', // Left align but with padding
    padding: '30px 0 0 20px', // Top padding and left padding
    margin: 0,
  },
  mobileNavList: {
    listStyle: 'none',
    padding: 0,
    margin: '40px 0 0 0', // Add top margin to position menu items lower
    width: '90%', // Not full width to prevent touching right edge
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start', // Left align items
  },
  mobileNavItem: {
    width: '100%',
    margin: '10px 0',
    textAlign: 'left', // Left align text
  },
  mobileNavLink: {
    color: 'white',
    textDecoration: 'none',
    fontWeight: '500',
    fontSize: '18px',
    padding: '15px 0',
    display: 'block',
    textTransform: 'uppercase',
  },
  mobileActiveNavLink: {
    color: 'white',
    textDecoration: 'none',
    fontWeight: '700',
    fontSize: '18px',
    padding: '15px 0',
    display: 'block',
    textTransform: 'uppercase',
  },
  mobileAdminButton: {
    background: 'transparent',
    border: 'none',
    borderRadius: '4px',
    color: 'white',
    fontWeight: '500',
    fontSize: '18px',
    padding: '10px 15px',
    margin: '15px 0',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    textTransform: 'uppercase',
  },
  // Desktop navigation styles
  navList: {
    listStyle: 'none',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '1.5rem',
    margin: 0,
    padding: 0,
    fontFamily: 'Inter, system-ui, sans-serif',
    maxWidth: 'calc(100% - 100px)',
    boxSizing: 'border-box',
  },
  navItem: {
    margin: 0,
    padding: 0,
  },
  navLink: {
    color: 'white',
    textDecoration: 'none',
    fontWeight: '500',
    fontSize: '14px',
    padding: '8px 0',
    position: 'relative',
    letterSpacing: '0.2px',
    transition: 'all 0.2s ease',
    opacity: '0.9',
    textTransform: 'uppercase',
    whiteSpace: 'normal',
    textAlign: 'center',
  },
  activeNavLink: {
    color: 'white',
    textDecoration: 'none',
    fontWeight: '700',
    fontSize: '14px',
    padding: '8px 0',
    position: 'relative',
    letterSpacing: '0.2px',
    opacity: '1',
    textTransform: 'uppercase',
    borderBottom: '2px solid white',
    whiteSpace: 'normal',
    textAlign: 'center',
  },
  adminButtonContainer: {
    position: 'absolute',
    right: '1rem',
    top: '20px',
    display: 'flex',
    alignItems: 'center',
    zIndex: 10001,
  },
  adminButton: {
    background: 'transparent',
    border: 'none',
    borderRadius: '4px',
    color: 'white',
    fontWeight: '500',
    fontSize: '14px',
    padding: '8px 12px',
    cursor: 'pointer',
    textTransform: 'uppercase',
    textDecoration: 'none',
    display: 'inline-block',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
  },
  facebookLink: {
    color: '#4267B2', // Facebook blue color
    fontWeight: 'bold',
    backgroundColor: 'rgba(255, 255, 255, 0.9)', // White background to make it more visible
    padding: '5px 10px',
    borderRadius: '5px',
    textTransform: 'none', // Prevent all caps in mobile menu
  },
};

// Add media queries at the end to override default styles for mobile
Object.assign(styles.navList, {
  '@media (max-width: 768px)': {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '75%',
    height: '100vh',
    backgroundColor: '#2b2e35', // Dark gray background as shown in the image
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    paddingTop: '80px',
    paddingLeft: '2rem',
    zIndex: 10000,
    transform: 'translateX(-100%)',
    transition: 'transform 0.3s ease-in-out',
    gap: '1rem',
  }
});

Object.assign(styles.navItem, {
  '@media (max-width: 768px)': {
    width: '100%',
    margin: '0.5rem 0',
  }
});

Object.assign(styles.navLink, {
  '@media (max-width: 768px)': {
    fontSize: '16px',
    padding: '12px 0',
    display: 'block',
    textAlign: 'left',
    width: '100%',
  }
});

Object.assign(styles.activeNavLink, {
  '@media (max-width: 768px)': {
    fontSize: '16px',
    padding: '12px 0',
    display: 'block',
    textAlign: 'left',
    width: '100%',
    borderBottom: 'none',
    fontWeight: '700',
  }
});
